import React, { useEffect, useState } from "react";
import { ArrowUpRight, Sparkles, Signal, Zap } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useMouseParallax, useTilt } from "@/hooks/useMouseParallax";

/**
 * SERVICES HERO — "Orbital Service Grid"
 * Mouse-parallax orbs, hex radar sweep, tilted services capsule.
 */
const ServicesHero = ({ onOpenAudit }) => {
  const { t, lang } = useLang();
  const { ref, pos } = useMouseParallax();
  const tiltRef = useTilt(8);

  const px = (mult) => ({ transform: `translate3d(${pos.x * mult}px, ${pos.y * mult}px, 0)` });

  const capsules = [
    { num: "01", label: lang === "bg" ? "SEO оптимизация" : "SEO optimization", pulse: "93" },
    { num: "02", label: lang === "bg" ? "Google Ads" : "Google Ads", pulse: "88" },
    { num: "03", label: lang === "bg" ? "Social Media" : "Social media", pulse: "96" },
    { num: "04", label: lang === "bg" ? "Branding" : "Branding", pulse: "91" },
    { num: "05", label: lang === "bg" ? "Уеб дизайн" : "Web design", pulse: "94" },
    { num: "06", label: lang === "bg" ? "AI автоматизация" : "AI automation", pulse: "97" },
  ];

  return (
    <section
      ref={ref}
      data-testid="services-hero"
      className="relative border-b border-brand-border overflow-hidden min-h-[88vh] flex items-center"
    >
      {/* Background layers */}
      <div className="absolute inset-0 hex-grid opacity-50 pointer-events-none" />
      <div className="absolute inset-0 starfield pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-purple w-[620px] h-[620px] -top-40 -left-24 opacity-30 animate-float-slow" style={px(-40)} />
        <div className="orb orb-pink w-[480px] h-[480px] -bottom-32 -right-24 opacity-25 animate-float-slower" style={px(30)} />
      </div>

      {/* Radar sweep */}
      <div className="absolute top-1/2 right-[8%] -translate-y-1/2 pointer-events-none hidden lg:block" style={px(20)}>
        <div className="relative w-[520px] h-[520px]">
          {[1, 2, 3, 4].map((r) => (
            <div
              key={r}
              className="absolute inset-0 rounded-full border border-brand-purple/20"
              style={{ transform: `scale(${r * 0.22 + 0.15})` }}
            />
          ))}
          <div className="absolute inset-0 rounded-full border border-brand-purple/30" />
          <div
            className="absolute top-1/2 left-1/2 w-1/2 h-[1px] origin-left"
            style={{
              background: "linear-gradient(90deg, transparent, #A855F7, #EC4899)",
              animation: "holo-rotate 6s linear infinite",
              transformOrigin: "left center",
            }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-brand-purple shadow-[0_0_30px_6px_rgba(168,85,247,0.7)] pulse-dot" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 pt-5 md:py-28 md:pt-7 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left column — intel */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-border bg-brand-surface/60 backdrop-blur-sm mb-8">
            <Signal className="w-3.5 h-3.5 text-brand-tealLight" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
              {lang === "bg" ? "// СИСТЕМА ОНЛАЙН" : "// SYSTEM ONLINE"}
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-brand-tealLight animate-pulse" />
          </div>

          <p className="text-xs font-mono uppercase tracking-[0.35em] text-brand-purpleLight mb-6 ml-1">{t.services.tag}</p>

          <h1 className="font-heading text-[3rem] md:text-[5.2rem] lg:text-[6.2rem] font-black uppercase leading-[0.88] tracking-tighter">
            <span className="block">{lang === "bg" ? "Стратегии" : "Strategies"}</span>
            <span className="block chrome-text">{lang === "bg" ? "с орбитална" : "with orbital"}</span>
            <span className="block holo-text glitch" data-text={lang === "bg" ? "точност" : "precision"}>
              {lang === "bg" ? "точност." : "precision."}
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-zinc-400 leading-relaxed text-base md:text-lg">
            {lang === "bg"
              ? "6 стратегически дисциплини, настроени като една екосистема. Всеки компонент работи синхронно, за да превърне трафика в приходи."
              : "6 strategic disciplines calibrated as a single ecosystem. Every component works in sync — turning traffic into revenue."}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={onOpenAudit}
              data-testid="services-hero-audit"
              className="inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-7 py-5 rounded-full text-xs"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "bg" ? "Безплатен AI audit" : "Free AI audit"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <a
              href="#services-list"
              className="inline-flex items-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-7 py-5 rounded-full text-xs"
            >
              {lang === "bg" ? "Към услугите" : "Explore stack"}
              <Zap className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Right column — tilted capsule */}
        <div className="lg:col-span-5">
          <div
            ref={tiltRef}
            className="relative rounded-[28px] p-[1px] holo-card transition-transform duration-200 will-change-transform"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="relative rounded-[27px] bg-brand-bg/85 backdrop-blur-xl border border-brand-border p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-purpleLight">
                  {lang === "bg" ? "// service.stack" : "// service.stack"}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">v.2026.1</span>
              </div>
              <ul className="space-y-2">
                {capsules.map((c) => (
                  <li
                    key={c.num}
                    className="group flex items-center gap-4 px-3 py-3 rounded-xl border border-brand-border hover:border-brand-purple/60 hover:bg-brand-surface/50 transition-all"
                  >
                    <span className="font-mono text-[10px] text-zinc-500 w-6">{c.num}</span>
                    <span className="flex-1 text-sm font-medium text-white">{c.label}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-1 w-16 rounded-full bg-brand-surface overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-brand-purple via-brand-pink to-brand-teal"
                          style={{ width: `${c.pulse}%` }}
                        />
                      </div>
                      <span className="font-mono text-[10px] text-brand-tealLight w-8 text-right">{c.pulse}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-brand-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-tealLight animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    {lang === "bg" ? "6/6 канала активни" : "6/6 channels live"}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-zinc-500">
                  {new Date().toISOString().slice(0, 10)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesHero;
